import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAudioPlayer } from "../../contexts/AudioPlayerContext";
import Modal from "../modal/Modal";
import InputWithButton from "../generic_components/InputWithButton";
import Toast from "../generic_components/Toast";
import { getCookie } from "../../utils/cookieUtils";
import {
  PlayFill,
  PauseFill,
  EllipsisVertical,
  ArrowUpRightFromSquare,
  TrashBin,
  ArrowShapeDownToLine,
  Check,
  StarFill,
  Star,
} from "@gravity-ui/icons";
import apiService from "../../api/apiService";
import AudioProcessor from "../custom_components/AudioProcessor";
import { usePostHog } from "posthog-js/react";
import ProgressInfoToast from "../custom_components/ProgressInfoToast";
import DownloadModal from "../custom_components/DownloadModal";
import { useLocalState } from "../../contexts/LocalStateProvider";
import * as Sentry from "@sentry/react"
import { getPartitionedLocalStorage } from "../../utils/tokenExtraction";
const ProjectListView = ({
  imageSrc,
  projectName,
  projectDescription,
  audioSrc,
  songTitle,
  projectId,
  responseId,
  onDelete,
  songDuration,
  lastUpdated,
  isSelectionMode,
  isSelected,
  onSelect,
  isStarred,
  onToggleStar,
  isLastItem = false,
  isSecondLastItem = false,
  projectsDataSource = null, // New prop for data source
}) => {
  const posthog = usePostHog();
  const { localState, updateLocalState } = useLocalState(); // Add updateLocalState
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeSong, setActiveSong] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("info");
  const dropdownRef = useRef(null);
  const dropdownButtonRef = useRef(null);
  const dropdownContainerRef = useRef(null);
  const navigate = useNavigate();
  const [pageWidth, setPageWidth] = useState(window.innerWidth);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  // Add state variables for stem generation
  const [isGeneratingStems, setIsGeneratingStems] = useState(false);
  const [stemProgress, setStemProgress] = useState(0);
  const [stemTrackName, setStemTrackName] = useState('');

  // Determine if a specific track version is selected for this project item
  const isTrackVersionSelected = useMemo(() => {
    const projectsToSearch = projectsDataSource || localState.cachedProjects || [];
    const matchingProject = projectsToSearch.find(
      (project) => String(project.response_id) === String(responseId)
    );
    const val = matchingProject?.selected_track_id || 0
    return val;
  }, [localState.selectedTrackDetails, responseId, projectsDataSource]);

  const {
    isPlaying,
    playAudio,
    pauseAudio,
    resumeAudio,
    audioSrc: currentAudioSrc,
    setAiResponseId,
    setDiscoverId,
  } = useAudioPlayer();

  useEffect(() => {
    if (!currentAudioSrc) {
      setActiveSong(null);
    }
  }, [currentAudioSrc]);

  useEffect(() => {
    const handleResize = () => {
      setPageWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleDownloadProjectSong = () => {
    posthog.capture("Click-Download-ProjectList-ProjectPage",{is_user_logged_in: true});
    setIsDownloadModalOpen(true);
  };

  // Handle Play/Pause
  const handlePlayPause = useCallback(
    (event) => {
      console.log('[ProjectListView] handlePlayPause triggered.');
      console.log('[ProjectListView] Original audioSrc prop:', audioSrc);
      console.log('[ProjectListView] responseId:', responseId);
      console.log('[ProjectListView] songTitle:', songTitle);
      console.log('[ProjectListView] localState.selectedTrackDetails:', localState.selectedTrackDetails);

      const projectsToSearch = projectsDataSource || localState.cachedProjects || [];
      const matchingProject = projectsToSearch.find(
        (project) => String(project.response_id) === String(responseId)
      );

      let trackIndexToPlay = matchingProject?.selected_track_id || 0;

      console.log("Track index to play:", trackIndexToPlay);

      if (
        (dropdownButtonRef.current &&
          dropdownButtonRef.current.contains(event.target)) ||
        (dropdownContainerRef.current &&
          dropdownContainerRef.current.contains(event.target))
      ) {
        return;
      }

      if (!responseId) {
        setToastMessage("Project information is missing. Cannot play audio.");
        setToastType("error");
        console.log('[ProjectListView] No responseId available.');
        return;
      } setAiResponseId(responseId);

      // let trackIndexToPlay = 0; // Default to track 0
      // Use the memoized value here
      if (isTrackVersionSelected) {
        console.log(`[ProjectListView] Track ${trackIndexToPlay} is explicitly selected for responseId ${responseId}.`);
      } else {
        // If no track is selected, we'll use track 0 and update the selection
        console.log(`[ProjectListView] No track explicitly selected for responseId ${responseId}. Using default track 0.`);
        // Update selectedTrackDetails to track 0 for this responseId
        updateLocalState('selectedTrackDetails', {
          responseId: responseId,
          trackIndex: trackIndexToPlay
        });
      }

      // Always allow playback with the determined track index
      const playableSrcToAttempt = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/stream-audio/${responseId}/${trackIndexToPlay}`;
      console.log('[ProjectListView] Using explicitly selected track URL:', playableSrcToAttempt);

      if (isPlaying && currentAudioSrc === playableSrcToAttempt) {
        pauseAudio();
        setActiveSong(null);
        console.log('[ProjectListView] Pausing audio.');
      } else if (currentAudioSrc === playableSrcToAttempt) {
        resumeAudio();
        setActiveSong(playableSrcToAttempt);
        console.log('[ProjectListView] Resuming audio.');
      } else {
        setDiscoverId(null);
        playAudio(
          playableSrcToAttempt,
          songTitle,
          projectDescription,
          imageSrc,
          songDuration
        );
        setActiveSong(playableSrcToAttempt);
        console.log('[ProjectListView] Playing new audio with src:', playableSrcToAttempt);
      }
    },
    [
      audioSrc,
      responseId,
      songTitle,
      projectDescription,
      imageSrc,
      songDuration,
      currentAudioSrc,
      isPlaying, pauseAudio,
      playAudio,
      resumeAudio,
      projectName,
      setAiResponseId,
      setDiscoverId,
      localState.selectedTrackDetails,
      isTrackVersionSelected, // Add memoized value to dependencies
      updateLocalState // Add updateLocalState to dependencies
    ]
  );

  // Toggle dropdown
  const handleDropdownToggle = (event) => {
    event.stopPropagation(); // Prevents triggering play/pause when dropdown button is clicked
    if (!showDropdown)
      posthog.capture("Project Card - Click on More Option Icon to view Menu",{is_user_logged_in: true});
    setShowDropdown((prev) => !prev);
  };

  // Click outside to close dropdown
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleGoToProject = () => {
    if (isSelectionMode) return;
    
    // Save scroll position before navigating away from projects page
    const projectsScrollContainer = document.querySelector('[data-projects-scroll-container]');
    if (projectsScrollContainer) {
      localStorage.setItem('projectsPageScrollPosition', projectsScrollContainer.scrollTop.toString());
    }
    
    posthog.capture("Click on Go To Project",{is_user_logged_in: true});
    navigate(`/chatPage?projectId=${projectId}`);
  };

  const handleDeleteProject = () => {
    setShowDeleteModal(true);
  };

  const closeModal = () => {
    setShowDeleteModal(false);
  };

  const confirmDeleteProject = async () => {
    posthog.capture("Click on Confirm Delete Project", {is_user_logged_in: true});

    try {
      const result = await apiService.sendRequest("deleteProject", {
        projectIds: [projectId],
      });

      if (result.success) {
        onDelete(projectId);
      } else {
        setToastMessage(
          result.error || "An error occurred while deleting the project."
        );
        setToastType("error");
      }
    } catch (error) {
      posthog.capture("Project Deletion Failed",{is_user_logged_in: true});
      Sentry.captureException(error);
      setToastMessage("An error occurred while deleting the project.");
      setToastType("error");
    } finally {
      setShowDeleteModal(false);
    }
  };

  const formatTime = (durationInSeconds) => {
    const minutes = Math.floor(durationInSeconds / 60);
    const seconds = Math.floor(durationInSeconds % 60);
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  const truncatedText = (text, length = 50) => {
    if (text.length <= length) return text;
    return text.slice(0, length) + "...";
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const updated = new Date(timestamp);
    const diffInSeconds = Math.floor((now - updated) / 1000);

    if (diffInSeconds < 60) return "just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800)
      return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return `${Math.floor(diffInSeconds / 604800)}w ago`;
  };

  // Add the handleGetStems function
  const handleGetStems = async (e) => {
    e.stopPropagation();
    if (!isTrackVersionSelected) {
      setToastMessage("Please select a track version within the project to get stems.");
      setToastType("info");
      setShowDropdown(false); // Optionally close dropdown
      return;
    }
    posthog.capture("Click-GetStems-ProjectList-ProjectPage",{is_user_logged_in: true});
    setIsGeneratingStems(true);
    setStemProgress(0);
    setToastMessage(null);

    console.log(`[ProjectListView] Initiating stem generation for response ID: ${responseId}`);

    try {
      // 1. Fetch GCS URI
      const gcsResponse = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/ai-response-gcs-uri/${responseId}/0`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`
          }
        }
      );
      const gcsData = await gcsResponse.json();

      if (!gcsData.success || !gcsData.gcsUri) {
        console.error("[ProjectListView] GCS URI Extraction Failed:", gcsData);
        setToastMessage(gcsData.message || "Could not find audio file URI.");
        setToastType("error");
        setIsGeneratingStems(false);
        return;
      }
      const gcsUri = gcsData.gcsUri;
      console.log(`[ProjectListView] GCS URI: ${gcsUri}`);

      // 2. Call process-audio endpoint
      const requestId = Date.now().toString();
      setToastMessage(`${stemTrackName || 'Track'} - Stems are being generated...`);
      setToastType("info");
      setStemProgress(1);

      const processResponse = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/process-audio`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`
          },
          body: JSON.stringify({ gcs_uri: gcsUri, request_id: requestId })
        }
      );
      const processData = await processResponse.json();

      if (processResponse.ok && processData.success) {
        console.log("[ProjectListView] Stem separation successful:", processData);
        setStemProgress(3);
        setToastMessage("Stems generated! Downloading...");
        setToastType("info");

        // 3. Download stems
        const stemPaths = Object.values(processData.uploaded_files);
        const downloadResponse = await fetch(
          `${process.env.REACT_APP_SERVER_ENDPOINT}/api/download-stems`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`
            },
            body: JSON.stringify({ stems: stemPaths })
          }
        );

        if (downloadResponse.ok) {
          const blob = await downloadResponse.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const finalName = songTitle || 'Audio';
          a.download = `${finalName}_Stems_by_Wubble.zip`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          setToastMessage("Stems downloaded successfully!");
          setToastType("success");
        } else {
          console.error("[ProjectListView] Error downloading stems:", downloadResponse);
          setToastMessage("Stems generated but download failed.");
          setToastType("error");
        }
      } else {
        console.error("[ProjectListView] Stem separation failed:", processData);
        setToastMessage(processData.message || processData.details || "Stem separation failed.");
        setToastType("error");
      }
    } catch (error) {
      console.error("[ProjectListView] Error during stem process:", error);
      Sentry.captureException(error);
      setToastMessage("An error occurred during stem generation.");
      setToastType("error");
    } finally {
      setIsGeneratingStems(false);
    }
  };

  return (
    <>
      <div
        id="project-card"
        className={`project-item p-[1px] rounded-xl m-1.5 z-0 ${isSelected ? "bg-gradient-to-r from-[#FC10F2] to-[#3078E4]" : ""
          }`}
      >
        <div
          ref={dropdownRef}
          onClick={handleGoToProject} // Play/Pause on div click
          className={`flex items-center p-1.5 pr-4 rounded-xl cursor-pointer group relative ${isPlaying && currentAudioSrc === activeSong
            ? "bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 text-white"
            : "hover:bg-opacity-10 transition duration-500 hover:bg-white"
            }`}
          style={
            isPlaying && currentAudioSrc === activeSong
              ? {
                background:
                  "linear-gradient(0deg, rgba(0, 0, 0, 0.72) 0%, rgba(0, 0, 0, 0.72) 100%), linear-gradient(135deg, #FC10F2 0%, #3078E4 100%)",
              }
              : isSelected
                ? {
                  background:
                    "linear-gradient(0deg, rgba(0, 0, 0, 0.72) 0%, rgba(0, 0, 0, 0.72) 100%), linear-gradient(135deg, #FC10F2 0%, #3078E4 100%)",
                }
                : {}
          }
        >
          <div className="flex items-center relative overflow-hidden mr-4">
            <img
              src={imageSrc}
              alt={projectName}
              className="w-12 h-12 object-cover rounded-lg"
            />
            <button
              id="project-play-pause-button"
              onClick={(e) => {
                e.stopPropagation();
                handlePlayPause(e);
              }}
              className={`absolute inset-0 flex items-center justify-center text-white transition-transform duration-500 transform group-hover:opacity-100 ${isPlaying && currentAudioSrc === activeSong
                ? "md:translate-y-0"
                : "md:translate-y-[70%] md:group-hover:translate-y-0"
                }`}
            >
              {isPlaying && currentAudioSrc === activeSong ? (
                <PauseFill className="w-4 h-4" />
              ) : (
                <PlayFill className="w-4 h-4" />
              )}
            </button>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-base truncate w-3/4">
              {projectName}
            </p>
            <p className="font-light text-sm text-gray-400">
              {pageWidth <= 768
                ? truncatedText(projectDescription, 24)
                : projectDescription}
            </p>
          </div>
          <div className="flex md:flex-1 items-center">
            <div className="hidden md:flex items-center mx-4 flex-grow text-center">
              <span className="font-light text-sm text-white">
                {formatTime(songDuration) || "0:00"}
              </span>
            </div>
            <div className="hidden md:flex flex-1 items-center text-[#878188] text-sm">
              <span className="font-extralight text-sm pt-2">
                <span className="text-[#C5C3C6]">Edited</span>{" "}
                <span className="text-white">{formatTimeAgo(lastUpdated)}</span>
              </span>
            </div>
            <div
              className="flex items-center gap-1 relative "
              onClick={handleDropdownToggle}
            >
              <div
                className={`flex relative w-8 h-8 mr-1 items-center justify-center checkbox-container md:invisible md:group-hover:visible transition-opacity duration-300`}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleStar();
                  }}
                  disabled={isSelectionMode}
                  className={`star-button w-8 h-8 flex items-center justify-center ${isSelectionMode && "cursor-not-allowed"
                    } ${isStarred
                      ? "star-fill bg-white/10 text-white hover:bg-white/15 border-none"
                      : "star rounded-full bg-white/5 hover:bg-white/10 text-white/40 hover:text-white"
                    }`}
                >
                  {isStarred ? (
                    <StarFill className="w-4 h-4" />
                  ) : (
                    <Star className="w-4 h-4" />
                  )}
                </button>
              </div>
              <div
                className={`flex relative w-8 h-8 items-center justify-center ${isSelected
                  ? "visible"
                  : "checkbox-container md:invisible md:group-hover:visible"
                  } transition-opacity duration-300`}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onSelect();
                  }}
                  className={` w-5 h-5 rounded-md appearance-none border ${isSelected
                    ? "bg-gradient-to-r from-[#FC10F2] to-[#3078E4] border-none"
                    : "border-white/20 bg-white border-grey-300"
                    }`}
                />
                <button onClick={onSelect}>
                  <Check
                    className={` absolute top-1/2 left-4 transform -translate-x-1/2 -translate-y-1/2  ${isSelected ? "text-white" : "text-gray-400"
                      }`}
                  />
                </button>
              </div>
              <button
                id="project-options-button"
                className={`rounded-full w-8 h-8 text-white/40 p-2 bg-transparent ${!isSelectionMode
                  ? `hover:bg-white/5 hover:text-white`
                  : `cursor-not-allowed`
                  } relative`}
                onClick={handleDropdownToggle} // Opens dropdown on ellipsis button click
                ref={dropdownButtonRef}
                disabled={isSelectionMode}
              >
                <EllipsisVertical />
              </button>
            </div>
            {/* Checkbox for selection */}
            {showDropdown && (
              <div
                id="project-options-dropdown"
                ref={dropdownContainerRef}
                className={`absolute ${isLastItem || isSecondLastItem ? 'bottom-10 mb-1' : 'top-8'} right-0 text-center font-normal text-sm text-[#878188] bg-[#100f10] p-1 rounded-lg border border-solid border-[#2c2a2c] w-36 z-10`}
              >
                <button
                  id="project-go-to-button"
                  onClick={handleGoToProject}
                  className="flex items-center gap-2 h-8 px-2 w-full hover:text-white rounded-lg hover:bg-[#1d1b1d]"
                >
                  <ArrowUpRightFromSquare className="w-4 h-4" /> Go to project
                </button>
                {/* {audioSrc && (
                  <>
                    <button
                      id="project-download-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isTrackVersionSelected) {
                          setToastMessage("Please select a track version within the project to download.");
                          setToastType("info");
                          setShowDropdown(false); // Optionally close dropdown
                          return;
                        }
                        handleDownloadProjectSong();
                      }}
                      disabled={!isTrackVersionSelected || isGeneratingStems} // Disable if no track selected or stems are generating
                      className={`flex items-center gap-2 h-8 px-2 w-full rounded-lg hover:bg-[#1d1b1d] ${!isTrackVersionSelected || isGeneratingStems
                        ? "text-gray-500 cursor-not-allowed hover:text-gray-500"
                        : "hover:text-white"
                        }`}
                    >
                      <ArrowShapeDownToLine className="w-4 h-4" /> Download
                    </button>
                    <button
                      id="project-get-stems-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Redundant check here as it's in handleGetStems, but good for direct click handling
                        if (!isTrackVersionSelected) {
                          setToastMessage("Please select a track version within the project to get stems.");
                          setToastType("info");
                          setShowDropdown(false); // Optionally close dropdown
                          return;
                        }
                        handleGetStems(e);
                      }}
                      disabled={!isTrackVersionSelected || isGeneratingStems} // Disable if no track selected or stems are generating
                      className={`flex items-center gap-2 h-8 px-2 w-full rounded-lg hover:bg-[#1d1b1d] ${!isTrackVersionSelected || isGeneratingStems
                        ? "text-gray-500 cursor-not-allowed hover:text-gray-500"
                        : "hover:text-white"
                        }`}
                    >
                      <img
                        src="/updates-modal/feature-update-icon-2.svg"
                        alt="Get stems"
                        className="w-4 h-4"
                      /> Get stems
                    </button>
                  </>
                )} */}
                {!isStarred && (
                  <button
                    id="project-delete-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProject();
                    }}
                    className="flex items-center gap-2 h-8 px-2 w-full hover:text-white rounded-lg hover:bg-[#1d1b1d]"
                  >
                    <TrashBin className="w-4 h-4" /> Delete project
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {showDeleteModal && (
        <Modal closeModal={closeModal}>
          <div className="text-white w-full pb-3 bg-[#1d1b1d] border border-solid border-[#3c393c] rounded-lg">
            <h3 className="text-left text-lg p-3" id="delete-modal-title">
              Delete project
            </h3>
            <div className="w-full border-t border-t-[#2d2a2d]"></div>
            <InputWithButton
              formClassName="space-y-4 w-full px-3"
              inputFields={[
                {
                  type: "info",
                  content: ["Are you sure you want to delete this project?"],
                  subContent:
                    "This will delete all the generated music in this project. Do you want to proceed?",
                  className:
                    "text-white text-left text-sm font-extralight mb-4 mt-3",
                },
              ]}
              buttonClassName="px-3 mr-3 h-8 text-sm bg-red-600 hover:bg-[#BB2D2D] text-white rounded-full"
              buttonText="Delete"
              onButtonClick={confirmDeleteProject} // Call confirmDeleteProject when delete is clicked
              cancelButtonClassName="px-3 h-8 text-sm rounded-full hover:[background:var(--Color-Opacity-White-5,rgba(255,255,255,0.05))] text-white"
              cancelButtonText="Cancel"
              onCancelClick={closeModal}
              closeModal={closeModal}
              cancelButtonId="delete-project-cancel-button"
              actionButtonId="delete-project-confirm-button"
              requestData={{
                taskName: "deleteProject",
                payload: {
                  projectIds: [projectId],
                },
              }}
            />
          </div>
        </Modal>
      )}

      {isGeneratingStems ? (
        <ProgressInfoToast
          type="info"
          message={`${stemTrackName || 'Track'} - Stems are being generated...`}
          loading={true}
          excludeCloseButton={false}
          className="-translate-x-1/2 w-[90%] md:w-fit md:min-w-[360px]"
          imageSrc={imageSrc}
          currentStep={stemProgress}
          totalSteps={3}
          onClose={() => setIsGeneratingStems(false)}
        />
      ) : toastMessage && (
        <Toast
          type={toastType}
          message={toastMessage}
          onClose={() => setToastMessage("")}
        />
      )}

      {isDownloadModalOpen && (
        <DownloadModal
          title={songTitle}
          description={projectDescription}
          duration={typeof songDuration === "string" ? songDuration : formatTime(songDuration) || "0:00"}
          imageSrc={imageSrc}
          ai_response_id={responseId}
          onClose={() => setIsDownloadModalOpen(false)}
          onError={(message) => {
            setToastMessage(message);
            setToastType("error");
            setIsDownloadModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default ProjectListView;
